import { HOURS, makeRange } from "./constants";
import { DayOfTheWeek, Daypart } from "./types";
export { makeRange } from "./constants";

/**
 * whether all hours are selected for a given daypart(s).
 * @param dayparts - array of dayparts
 * @param day
 * - if provided, only checks if all hours are selected for that day.
 * - if not provided, checks if all hours are selected for all days.
 * @returns boolean
 */
export const hasAllDaypartsSelected = (
  dayparts: Daypart[] = [],
  day?: DayOfTheWeek
) => {
  if (day) {
    const daypart = dayparts.find(d => d.day === day);
    return daypart?.hours.length === HOURS.length;
  }
  return dayparts.flatMap(d => d.hours).length === HOURS.length * 7;
};

/**
 * whether no hours are selected for a given daypart(s).
 * @param dayparts - array of dayparts
 * @param day
 * - if provided, only checks if all hours are selected for that day.
 * - if not provided, checks if all hours are selected for all days.
 * @returns boolean
 */
export const hasNoDaypartsSelected = (
  dayparts: Daypart[] = [],
  day?: DayOfTheWeek
) => {
  if (day) {
    const daypart = dayparts.find(d => d.day === day);
    return daypart?.hours.length === HOURS.length;
  }
  return dayparts.flatMap(d => d.hours).length === 0;
};

const HOURS_ARR = makeRange(24);

/**
 * @param dayparts: a set of dayparts.
 * @return: the mirror opposite of that daypart set. Ex: input: [{monday, hours: [0-22]}]; output: [{monday, hours: [23]}, (...all other days with full hours)]
 */
export const invertDayparts = <
  T extends Daypart[] | { [dayOfWeek in string]: number[] }
>(
  dayparts: T
): T => {
  const emptyDaypartsObject = {
    Sunday: { day: DayOfTheWeek.SUNDAY, hours: [] },
    Monday: { day: DayOfTheWeek.MONDAY, hours: [] },
    Tuesday: { day: DayOfTheWeek.TUESDAY, hours: [] },
    Wednesday: { day: DayOfTheWeek.WEDNESDAY, hours: [] },
    Thursday: { day: DayOfTheWeek.THURSDAY, hours: [] },
    Friday: { day: DayOfTheWeek.FRIDAY, hours: [] },
    Saturday: { day: DayOfTheWeek.SATURDAY, hours: [] }
  };

  const daypartsArr: Daypart[] = Array.isArray(dayparts)
    ? dayparts
    : daypartsFromObject(dayparts);

  /**
   * If the user only selected hours on certain days, they won't appear in the
   * array passed into this function. So we construct an array with default values for every day */
  const daypartsWithAllDays: Daypart[] = Object.values(
    daypartsArr.reduce((filledDaypartsObject, daypart) => {
      return {
        ...filledDaypartsObject,
        [daypart.day]: daypart
      };
    }, emptyDaypartsObject)
  );

  // invert hours, and exclude any days whose inverted hour count leaves them with 0 hours.
  const invertedDayparts: Daypart[] = daypartsWithAllDays.reduce(
    (invertedDaypartAcc: Daypart[], daypart: Daypart) => {
      const invertedHours = invertDaypartHours(daypart.hours);

      if (invertedHours.length > 0) {
        const invertedDaypart = { day: daypart.day, hours: invertedHours };
        invertedDaypartAcc.push(invertedDaypart);
      }

      return invertedDaypartAcc;
    },
    []
  );

  return (
    Array.isArray(dayparts)
      ? invertedDayparts
      : daypartsToObject(invertedDayparts)
  ) as T;
};

/**
 * Converts `Daypart[]` to `{ [day: string]: number[] }`
 */
export function daypartsToObject(dayparts: Daypart[]): {
  [dayOfWeek in string]: number[];
} {
  return dayparts.reduce(
    (acc, { day, hours }) => {
      acc[day] = hours;
      return acc;
    },
    {} as { [dayOfWeek in string]: number[] }
  );
}

/**
 * Converts `{ [day: string]: number[] }` to `Daypart[]`
 */
export function daypartsFromObject(dayparts: {
  [dayOfWeek in string]: number[];
}): Daypart[] {
  return Object.entries(dayparts).map(([day, hours]) => ({
    day: day as DayOfTheWeek,
    hours
  }));
}

/**
 * Inverts the given array of hours, returning the hours not included in the input.
 * If the input is undefined or empty, returns all 24 hours.
 */
export function invertDaypartHours(hours: number[] | undefined): number[] {
  if (!hours || hours.length === 0) {
    return [...HOURS_ARR];
  }
  const selectedHoursSet = new Set(hours);
  return HOURS_ARR.filter(hour => !selectedHoursSet.has(hour));
}
